import React, { useMemo } from "react";
import { ArrowheadMarker } from "@csk/demo-ui";
import type { BridgeMatrix } from "../types";

interface Node {
  id: string;
  x: number;
  y: number;
}
interface Edge {
  from: string;
  to: string;
  status: "allowed" | "forbidden";
}
interface DrawEdge {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  stroke: string;
  marker: string;
}

const ALLOWED_STROKE = "rgba(34,197,94,0.7)";
const FORBIDDEN_STROKE = "rgba(239,68,68,0.7)";
const ALLOWED_MARKER = "url(#arrowAllowed)";
const FORBIDDEN_MARKER = "url(#arrowForbidden)";

export function MatrixGraph(props: {
  matrix: BridgeMatrix;
}): React.JSX.Element {
  const { nodes, drawEdges } = useMemo(() => {
    const edges: Edge[] = [];
    for (const r of props.matrix.rules) {
      edges.push({
        from: r.lhs,
        to: r.rhs,
        status: r.status,
      });
    }
    const nodes: Node[] = [];
    const idx = new Map<string, Node>();
    for (const [i, id] of props.matrix.index.nodes.entries()) {
      const node = {
        id,
        x: 120 + (i % 3) * 260,
        y: 80 + Math.floor(i / 3) * 140,
      };
      nodes.push(node);
      idx.set(id, node);
    }
    const drawEdges: DrawEdge[] = [];
    edges.forEach((edge, i) => {
      const a = idx.get(edge.from);
      const b = idx.get(edge.to);
      if (!a || !b) return;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.max(1, Math.sqrt(dx * dx + dy * dy));
      const ux = dx / len;
      const uy = dy / len;
      drawEdges.push({
        id: `${edge.from}->${edge.to}#${i}`,
        x1: a.x + ux * 46,
        y1: a.y + uy * 22,
        x2: b.x - ux * 46,
        y2: b.y - uy * 22,
        stroke: edge.status === "allowed" ? ALLOWED_STROKE : FORBIDDEN_STROKE,
        marker: edge.status === "allowed" ? ALLOWED_MARKER : FORBIDDEN_MARKER,
      });
    });
    return { nodes, drawEdges };
  }, [props.matrix]);

  const w = 900;
  const h = Math.max(420, 120 + Math.ceil(nodes.length / 3) * 140);

  return (
    <div className="hub-panel-surface-muted p-3 overflow-x-auto">
      <svg
        width={w}
        height={h}
        role="img"
        aria-label="Bridge interaction graph"
      >
        <defs>
          <ArrowheadMarker
            id="arrowAllowed"
            color="rgba(34,197,94,0.85)"
            size={10}
            orient="auto"
          />
          <ArrowheadMarker
            id="arrowForbidden"
            color="rgba(239,68,68,0.85)"
            size={10}
            orient="auto"
          />
        </defs>

        {drawEdges.map((edge) => (
          <line
            key={edge.id}
            x1={edge.x1}
            y1={edge.y1}
            x2={edge.x2}
            y2={edge.y2}
            stroke={edge.stroke}
            strokeWidth={2}
            markerEnd={edge.marker}
          />
        ))}

        {nodes.map((n) => (
          <g key={n.id} transform={`translate(${n.x},${n.y})`}>
            <rect
              x={-70}
              y={-22}
              width={140}
              height={44}
              rx={14}
              fill="rgba(15,23,42,0.9)"
              stroke="rgba(148,163,184,0.25)"
            />
            <text
              x="0"
              y="5"
              textAnchor="middle"
              fill="rgba(226,232,240,0.95)"
              fontSize="12"
              fontFamily="ui-sans-serif, system-ui"
            >
              {n.id}
            </text>
          </g>
        ))}
      </svg>

      <div className="mt-2 text-xs text-slate-400">
        Green edges are allowed compositions. Red edges are forbidden. Unknown
        pairs are forbidden by default (fail-closed).
      </div>
    </div>
  );
}
